import { useState } from "react";
import { AnimatePresence } from "framer-motion";
import {
  Reveal,
  Stagger,
  StaggerItem,
  SectionNo,
  Hairline,
  MaskReveal,
} from "@/components/primitives";
import { useScroll } from "@/lib/scroll";
import LivingImage from "@/components/LivingImage";
import BigSectionNo from "@/components/BigSectionNo";
import Lightbox from "@/components/Lightbox";
import FilmPlayer from "@/components/FilmPlayer";

// Demonstration frames — treatment examples, never client work. Two cards
// carry their film inline as a quiet loop; the rest hold the still.
const WORKS = [
  { id: "S24", type: "Beach · morning", meta: "21:9 MASTER", ratio: "aspect-[21/9]", film: "F17" },
  { id: "S25", type: "Dining · terrace", meta: "4:5 PORTRAIT", ratio: "aspect-[4/5]", film: "F18" },
  { id: "S27", type: "Destination · desert", meta: "16:9 MASTER", ratio: "aspect-video lg:aspect-auto", film: "F20" },
  { id: "S26", type: "Suite · interior", meta: "4:5 PORTRAIT", ratio: "aspect-[4/5]", film: "F19" },
  { id: "S23", type: "Wellness · dawn", meta: "16:9 MASTER", ratio: "aspect-video lg:aspect-auto", film: "F16" },
  { id: "S19", type: "Pool · sunset", meta: "21:9 MASTER", ratio: "aspect-[21/9]" },
  { id: "W05", type: "Suite · portrait", meta: "9:16 VERTICAL", ratio: "aspect-[3/4]", film: "W05" },
  { id: "S10", type: "Landscape · night", meta: "21:9 MASTER", ratio: "aspect-[21/9] lg:aspect-auto", film: "F12" },
];

// Autoplaying muted loop, gated globally by the LivingImage registry.
function WorkCard({ item, onOpen, sizes }) {
  return (
    <LivingImage
      still={item.id}
      film={item.film}
      sizes={sizes}
      onClick={onOpen}
      testId={`work-frame-${item.id.toLowerCase()}`}
      alt={`${item.type}, open demonstration frame`}
      wrapperClassName={`group relative block h-full w-full rounded-[2px] border border-hairline text-left glide hover:border-gold/50 ${item.ratio}`}
      className="atmos"
      parallax
    >
      {/* dual scrim: strong at the bottom (labels), soft at the top
          (corner markers) — legibility survives every frame */}
      <div className="absolute inset-x-0 bottom-0 h-[62%] bg-gradient-to-t from-ink/92 via-ink/45 to-transparent" aria-hidden="true" />
      <div className="absolute inset-x-0 top-0 h-[22%] bg-gradient-to-b from-ink/45 to-transparent" aria-hidden="true" />
      <div className="card-sweep" aria-hidden="true" />
      <div className="on-dark absolute inset-x-0 bottom-0 flex items-end justify-between p-5">
        <div>
          <div className="display text-2xl text-primaryText">{item.type}</div>
          <div className="mt-1 font-mono text-[9px] tracking-[0.2em] text-gold">
            {item.film ? "DEMONSTRATION FILM · LOOPING" : "DEMONSTRATION FRAME"}
          </div>
        </div>
        <span className="font-mono text-[10px] tracking-widest text-mutedText">
          {item.meta}
        </span>
      </div>
      <span className="absolute right-4 top-4 h-3 w-3 border-r border-t border-brass/40" />
      <span className="absolute left-4 top-4 h-3 w-3 border-l border-t border-brass/40" />
      {item.film && <span aria-hidden="true" data-cursor="play" className="absolute inset-0" />}
    </LivingImage>
  );
}

export default function SelectedWork({ films: clientFilms = [] }) {
  const { scrollTo } = useScroll();
  const [open, setOpen] = useState(null);
  const hasFilms = Array.isArray(clientFilms) && clientFilms.length > 0;

  return (
    <section id="work" className="relative px-6 py-20 sm:px-10 md:py-28 warm-radial" data-testid="selected-work">
      <BigSectionNo n="04" />
      <div className="relative mx-auto max-w-[1280px]">
        <Hairline className="mb-20" />
        <Reveal>
          <SectionNo n="04" label="Selected work" />
        </Reveal>

        <div className="mt-8 grid gap-10 lg:grid-cols-[1fr_0.9fr] lg:items-end">
          <h2
            className="display text-primaryText"
            style={{
              fontSize: "clamp(2.25rem, 5vw, 4rem)",
              lineHeight: 1.02,
              letterSpacing: "-0.015em",
              fontWeight: 300,
            }}
          >
            <MaskReveal>
              {hasFilms ? "Selected work." : "Selected work is shared privately."}
            </MaskReveal>
          </h2>
          <Reveal delay={0.12}>
            <p className="max-w-md text-base leading-relaxed text-mutedText">
              {hasFilms
                ? "A small selection of recent films."
                : "Client films are shown on request rather than published. The frames below are demonstration material, the treatment applied to frames we own, so you can judge the standard without anyone's marketing sitting in a public gallery. Two of them are already moving."}
            </p>
          </Reveal>
        </div>

        {hasFilms ? (
          <Stagger className="mt-16 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {clientFilms.map((f, i) => (
              <StaggerItem key={i}>
                <FilmPlayer src={f.videoUrl} poster={f.poster} label={f.title} />
                <div className="mt-4 flex items-baseline justify-between">
                  <span className="display text-xl text-primaryText">{f.title}</span>
                  <span className="font-mono text-[11px] tracking-widest text-gold">
                    {f.location}
                  </span>
                </div>
              </StaggerItem>
            ))}
          </Stagger>
        ) : (
          <>
            <Stagger className="mt-16 grid gap-5 lg:grid-cols-3">
              <StaggerItem className="lg:col-span-3">
                <WorkCard item={WORKS[0]} onOpen={() => setOpen(0)} sizes="100vw" />
              </StaggerItem>
              <StaggerItem className="lg:col-span-1">
                <WorkCard
                  item={WORKS[1]}
                  onOpen={() => setOpen(1)}
                  sizes="(min-width: 1024px) 33vw, 100vw"
                />
              </StaggerItem>
              <StaggerItem className="lg:col-span-2 lg:h-auto">
                <WorkCard
                  item={WORKS[2]}
                  onOpen={() => setOpen(2)}
                  sizes="(min-width: 1024px) 66vw, 100vw"
                />
              </StaggerItem>
              <StaggerItem className="lg:col-span-1">
                <WorkCard
                  item={WORKS[3]}
                  onOpen={() => setOpen(3)}
                  sizes="(min-width: 1024px) 33vw, 100vw"
                />
              </StaggerItem>
              <StaggerItem className="lg:col-span-2 lg:h-auto">
                <WorkCard
                  item={WORKS[4]}
                  onOpen={() => setOpen(4)}
                  sizes="(min-width: 1024px) 66vw, 100vw"
                />
              </StaggerItem>
              <StaggerItem className="lg:col-span-3">
                <WorkCard item={WORKS[5]} onOpen={() => setOpen(5)} sizes="100vw" />
              </StaggerItem>
              <StaggerItem className="lg:col-span-1">
                <WorkCard
                  item={WORKS[6]}
                  onOpen={() => setOpen(6)}
                  sizes="(min-width: 1024px) 33vw, 100vw"
                />
              </StaggerItem>
              <StaggerItem className="lg:col-span-2 lg:h-auto">
                <WorkCard item={WORKS[7]} onOpen={() => setOpen(7)} sizes="(min-width: 1024px) 66vw, 100vw" />
              </StaggerItem>
            </Stagger>
            <Reveal delay={0.1}>
              <div className="mt-14 flex justify-center">
                <button
                  onClick={() => scrollTo("#enquiry")}
                  className="btn-sweep rounded-full border border-gold/60 px-8 py-4 text-sm tracking-wide text-primaryText transition-colors duration-300 hover:bg-gold/10"
                  data-testid="request-showcase"
                >
                  Request the showcase
                </button>
              </div>
            </Reveal>
            <AnimatePresence>
              {open !== null && (
                <Lightbox
                  items={WORKS}
                  index={open}
                  onClose={() => setOpen(null)}
                  onIndex={setOpen}
                />
              )}
            </AnimatePresence>
          </>
        )}
      </div>
    </section>
  );
}
